import React from 'react';
import { Outlet } from 'react-router-dom';
import { useFeaturePermission } from '@/hooks/useFeaturePermission';
import AccessDenied from './AccessDenied';

interface PermissionGuardProps {
    permission: string | string[];
    featureKey?: string;
    children?: React.ReactNode;
}

/**
 * PermissionGuard: wraps routes that require specific permissions.
 *
 * Uses usePermission hook which handles:
 * - Super roles (org_owner, org_admin) always pass
 * - Legacy sessions always pass (backward compat)
 * - Actual permission check for normal users
 *
 * If user lacks permission → shows AccessDenied component
 * If user has permission → renders child routes
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
    permission,
    featureKey,
    children,
}) => {
    const { hasAccess } = useFeaturePermission({ permission, featureKey });

    if (!hasAccess) {
        const permissionLabel = Array.isArray(permission)
            ? permission.join(' or ')
            : permission;
        const requirement = featureKey
            ? `${permissionLabel} + feature:${featureKey}`
            : permissionLabel;
        return <AccessDenied requiredPermission={requirement} />;
    }

    return children ? <>{children}</> : <Outlet />;
};

export default PermissionGuard;
